import React, { useMemo, useState } from 'react';
import Avatar from './Avatar';
import Rating from './Rating';
import type { ServerReview } from './ServerReviews';
import { useLanguage } from '../contexts/LanguageContext';

interface ServerReviewFormProps {
  currentUser: {
    id: string;
    username: string;
    displayName?: string | null;
    avatarUrl?: string | null;
  } | null;
  isAuthenticated: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (payload: { rating: number; content: string }) => Promise<boolean> | boolean;
  className?: string;
  existingReview?: ServerReview | undefined;
}

const MIN_CONTENT_LENGTH = 20;
const MAX_CONTENT_LENGTH = 1000;
const RATING_OPTIONS = [5, 4, 3, 2, 1];

const ServerReviewForm: React.FC<ServerReviewFormProps> = ({
  currentUser,
  isAuthenticated,
  isSubmitting,
  errorMessage = null,
  onSubmit,
  className = '',
  existingReview,
}) => {
  const { t } = useLanguage();
  const [rating, setRating] = useState<number>(existingReview?.rating ?? 5);
  const [content, setContent] = useState<string>('');
  const [touched, setTouched] = useState(false);

  const displayName = useMemo(() => {
    if (!currentUser) {
      return t('server.reviews.guest_name') || 'Guest adventurer';
    }

    return currentUser.displayName || currentUser.username;
  }, [currentUser, t]);

  const currentAvatar = currentUser?.avatarUrl ?? undefined;

  const contentTooShort = content.trim().length > 0 && content.trim().length < MIN_CONTENT_LENGTH;
  const contentTooLong = content.length > MAX_CONTENT_LENGTH;
  const hasExistingReview = !!existingReview;

  const isSubmitDisabled =
    !isAuthenticated ||
    isSubmitting ||
    hasExistingReview ||
    content.trim().length < MIN_CONTENT_LENGTH ||
    contentTooLong;

  const helperText = () => {
    if (!isAuthenticated) {
      return t('server.reviews.helper.signin') || 'Sign in to share your perspective with the community.';
    }

    if (hasExistingReview) {
      return t('server.reviews.helper.existing') || 'You already left a review for this server. Editing support is coming soon!';
    }

    if (contentTooShort) {
      return (t('server.reviews.helper.short') || 'Add a bit more detail. At least {0} characters required.').replace('{0}', MIN_CONTENT_LENGTH.toString());
    }

    if (contentTooLong) {
      return (t('server.reviews.helper.long') || 'That is a bit long. Keep it under {0} characters.').replace('{0}', MAX_CONTENT_LENGTH.toString());
    }

    return t('server.reviews.helper.default') || 'Share what you enjoyed, leave a rating, and help fellow players decide.';
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);

    if (isSubmitDisabled) {
      return;
    }

    const wasSuccessful = await onSubmit({ rating, content: content.trim() });

    if (wasSuccessful) {
      setContent('');
      setTouched(false);
      setRating(5);
    }
  };

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-6 shadow-lg ${className}`}>
      <div className="flex items-start gap-4">
        <Avatar size="md" alt={displayName} src={currentAvatar} className="mt-1" />
        <div className="flex-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-600 dark:text-blue-300">{displayName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('server.reviews.subtitle') || 'Support the community by sharing your experience.'}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-gray-500">{t('server.reviews.rating_label') || 'Rating'}</span>
              <div className="flex items-center gap-1">
                {RATING_OPTIONS.map((option) => {
                  const isActive = rating === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`rounded-md border px-2 py-1 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900 ${isActive ? 'border-yellow-400 bg-yellow-500/10 text-yellow-600 dark:text-yellow-300' : 'border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-yellow-400 hover:text-yellow-500 dark:hover:text-yellow-200'
                        }`}
                      onClick={() => setRating(option)}
                      disabled={!isAuthenticated || hasExistingReview}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {hasExistingReview && existingReview && (
            <div className="mt-4 rounded-md border border-blue-300 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-900/20 p-4 text-xs text-blue-700 dark:text-blue-100">
              <p className="font-semibold">{t('server.reviews.existing_title') || 'Your Review:'}</p>
              <div className="mt-2 flex items-center gap-2 text-blue-600 dark:text-blue-200">
                <Rating rating={existingReview.rating} size="sm" />
                <span className="text-xs text-blue-700 dark:text-blue-100">{existingReview.content}</span>
              </div>
              <p className="mt-2 text-[11px] text-blue-600 dark:text-blue-200">{t('server.reviews.editing_soon') || 'Editing/Updating feature is coming soon.'}</p>
            </div>
          )}

          {!hasExistingReview && (
            <form className="mt-4" onSubmit={handleSubmit}>
              <label htmlFor="server-review-content" className="sr-only">
                Review content
              </label>
              <textarea
                id="server-review-content"
                name="server-review-content"
                className={`min-h-[120px] w-full resize-y rounded-md border bg-gray-50 dark:bg-gray-800/60 px-3 py-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${touched && (contentTooShort || contentTooLong) ? 'border-red-600' : 'border-gray-300 dark:border-gray-700'
                  }`}
                placeholder={t('server.reviews.placeholder') || 'What stood out about this server? Any highlights or tips to share?'}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                onBlur={() => setTouched(true)}
                disabled={!isAuthenticated || isSubmitting}
              />
              <div className="mt-2 flex flex-col gap-2 text-xs text-gray-500 dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  {helperText()}
                </span>
                <span>{content.trim().length}/{MAX_CONTENT_LENGTH}</span>
              </div>

              {errorMessage && (
                <div className="mt-3 rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/40 px-3 py-2 text-xs text-red-700 dark:text-red-200">
                  {errorMessage}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  className="rounded-md bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-blue-500 hover:to-purple-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isSubmitDisabled}
                >
                  {isSubmitting ? (t('server.reviews.submitting') || 'Submitting...') : (t('server.reviews.submit') || 'Share review')}
                </button>
              </div>
            </form>
          )}

          {!isAuthenticated && (
            <div className="mt-4 rounded-md border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 text-xs text-yellow-700 dark:text-yellow-100">
              {t('server.reviews.guest_message') || 'You are browsing as a guest. Create an account or sign in to vote and share reviews.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServerReviewForm;

